/**
 * Карточка клиента консультанта (AK-979) — `/clients/$externalId?tab=…`.
 *
 * Вход — строка списка «Клиенты». Low-fi и порядок вкладок утверждены founder
 * 21.10 (PR #1176): Обзор · СПП · Осмотры · Визиты · Продажи · План продаж ·
 * Опыты. Вкладка живёт в адресе: ссылку на «Продажи» клиента можно отправить
 * коллеге, «назад» возвращает на ту же вкладку.
 *
 * На телефоне ряд вкладок листается вбок ВНУТРИ себя, страница вбок не
 * листается (как проверено на прототипе: 569 px вкладок в полосе 364 px).
 */
import { useEffect, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ClientActions } from '@/components/consultant/ClientActions';
import {
  FarmSetupDialog,
  type DepartmentOption,
} from '@/components/consultant/FarmSetupDialog';
import {
  ErrorBlock,
  InspectionsTab,
  OverviewTab,
  PlanTab,
  SalesTab,
  SppTab,
  TrialsTab,
  VisitsTab,
  useLoad,
} from '@/components/consultant/ClientCardTabs';
import { api } from '@/lib/api';
import {
  CLIENT_CARD_TABS,
  getClientCard,
  type ClientCardTab,
} from '@/lib/consultant/clientCard';
import { syncAgeLabel } from '@/lib/consultant/format';
import { useClientFlow } from '@/lib/consultant/useClientFlow';
import { useConsultantProfile } from '@/lib/consultant/useConsultantProfile';
import { consultantClientCardRoute, router } from '@/router';

export function ConsultantClientCardPage({ externalId }: { externalId: string }) {
  const search = consultantClientCardRoute.useSearch();
  const tab: ClientCardTab = search.tab ?? 'overview';
  const card = useLoad(() => getClientCard(externalId), [externalId]);
  const { profile } = useConsultantProfile();
  const windowTenantId = profile?.client_window?.tenant_id ?? null;
  const flow = useClientFlow(() => card.reload());
  const [setupOpen, setSetupOpen] = useState(false);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);

  useEffect(() => {
    if (!setupOpen || departments.length) return;
    api
      .get<DepartmentOption[]>('/api/v1/clusters')
      .then((rows) => setDepartments(Array.isArray(rows) ? rows : []))
      .catch(() => setDepartments([]));
  }, [setupOpen, departments.length]);

  const openTab = (next: ClientCardTab) => {
    void router.navigate({
      to: '/clients/$externalId',
      params: { externalId },
      search: { tab: next === 'overview' ? undefined : next },
      replace: true,
    });
  };

  const c = card.data;
  const sys = c?.system;
  const flowClient = sys
    ? { external_id: externalId, crm_company_id: sys.crm_company_id }
    : null;
  const subtitle = c
    ? [
        c.requisites.client_status,
        c.requisites.priority_letter ? `приоритет ${c.requisites.priority_letter}` : null,
        c.manager.name,
        syncAgeLabel(c.synced_at),
      ]
        .filter(Boolean)
        .join(' · ')
    : null;

  /** Вкладка, которой нужен блок `system` карточки, ждёт карточку. */
  const needsCard = (body: (s: NonNullable<typeof sys>) => React.ReactNode) => {
    if (card.loading && !c) return <Skeleton className="h-32 w-full" />;
    if (card.error || !sys) {
      return (
        <ErrorBlock kind={card.error ?? 'other'} onRetry={card.reload} testId="card" />
      );
    }
    return body(sys);
  };

  return (
    <AppShell>
      <Stack gap={4} className="min-w-0">
        <div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void router.navigate({ to: '/clients' })}
            data-testid="client-card-back"
          >
            <ChevronLeft className="mr-1 h-3.5 w-3.5" />
            Клиенты
          </Button>
        </div>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <Stack gap={1} className="min-w-0">
            {card.loading && !c ? (
              <Skeleton className="h-8 w-64" data-testid="client-card-loading" />
            ) : (
              <h1 className="text-xl font-semibold" data-testid="client-card-title">
                {c?.title ?? 'Клиент'}
              </h1>
            )}
            {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
          </Stack>
          {sys && flowClient && (
            <ClientActions
              state={sys.state}
              fieldsCount={sys.fields_count}
              clusterName={sys.cluster_name}
              busy={flow.busy}
              onSetupFarm={() => setSetupOpen(true)}
              onAddFields={() => {
                if (sys.state === 'farm_ready' || sys.state === 'in_system') {
                  void flow.goAddFields(flowClient);
                } else {
                  setSetupOpen(true);
                }
              }}
              onShowOnMap={() => void flow.goToMap(flowClient)}
            />
          )}
        </div>

        <Tabs value={tab} onValueChange={(v) => openTab(v as ClientCardTab)}>
          {/* Ряд вкладок листается вбок внутри своей полосы — страница нет. */}
          <div className="w-full overflow-x-auto" data-testid="client-card-tabs-scroll">
            <TabsList className="w-max justify-start">
              {CLIENT_CARD_TABS.map((t) => (
                <TabsTrigger key={t.value} value={t.value} data-testid={`client-tab-${t.value}`}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <TabsContent value="overview">
            {card.loading && !c ? (
              <Stack gap={3} data-testid="tab-overview-loading">
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-48 w-full" />
              </Stack>
            ) : card.error || !c ? (
              <ErrorBlock kind={card.error ?? 'other'} onRetry={card.reload} testId="card" />
            ) : (
              <OverviewTab card={c} onOpenTab={openTab} />
            )}
          </TabsContent>
          <TabsContent value="spp">
            {tab === 'spp' &&
              needsCard((s) => <SppTab system={s} windowTenantId={windowTenantId} />)}
          </TabsContent>
          <TabsContent value="inspections">
            {tab === 'inspections' &&
              needsCard((s) => (
                <InspectionsTab externalId={externalId} system={s} windowTenantId={windowTenantId} />
              ))}
          </TabsContent>
          <TabsContent value="visits">
            {tab === 'visits' && <VisitsTab externalId={externalId} />}
          </TabsContent>
          <TabsContent value="sales">{tab === 'sales' && <SalesTab externalId={externalId} />}</TabsContent>
          <TabsContent value="plan">
            <PlanTab />
          </TabsContent>
          <TabsContent value="trials">
            <TrialsTab />
          </TabsContent>
        </Tabs>
      </Stack>
      {setupOpen && c && (
        <FarmSetupDialog
          open
          onOpenChange={(open) => {
            if (!open) setSetupOpen(false);
          }}
          client={{
            title: c.title,
            external_id: externalId,
            inn: c.inn,
            city: c.requisites.city,
            region: c.requisites.region,
            assigned_by_name: c.manager.name,
            department_path: c.system.department_path,
            farm_name: c.system.farm_name,
            cluster_id: c.system.cluster_id,
          }}
          departments={departments}
          submitting={flow.busy}
          error={flow.error}
          onSubmit={async (result) => {
            if (!flowClient) return;
            const ok = await flow.applyFarmSetup(flowClient, result);
            if (ok && !result.addFields) setSetupOpen(false);
          }}
        />
      )}
    </AppShell>
  );
}
